import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { db } from '../data/store';
import { collection, doc, getDoc, setDoc, deleteDoc, updateDoc, onSnapshot, newId, writeBatch } from '@battleops/localstore';
import { normalizeRoutine, routineVisible, prepareRoutine, activeRun, todayRoutines, commands, textToHtml, localDateStr } from '@battleops/shared';
import { runProcess } from '../data/processRun';
import { useSession } from './SessionContext';
import { useTasks } from './TaskContext';
import { useProjects } from './ProjectContext';
import { useDepartments } from './DepartmentContext';
import { useNotes } from './NoteContext';
import { runRoutineHooks, onRoutineHook } from '../data/routineHooks';
import { fireRoutineEvent } from '../data/routineEvents';

// Routines (docs/plans/routines.md) — the old Processes rebuilt in place: `processes` holds the routines,
// `processExecutions` their runs. Run actions go through the shared commands (one group each) via
// TaskContext.runClockCommand, so the clock and the step timer move with the run.
const ProcessContext = createContext();
const PROCESSES = 'processes';
const RUNS = 'processExecutions';

export function ProcessProvider({ children }) {
  const uid = useSession()?.identity?.uid || null;
  const { runClockCommand, tasks } = useTasks();
  const { projects } = useProjects();
  const { rawDepartments, departments } = useDepartments();
  const { addNote, loadBody, saveBody } = useNotes();
  const [raw, setRaw] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const u1 = onSnapshot(collection(db, PROCESSES), (snap) => { setRaw(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoaded(true); }, () => { setRaw([]); setLoaded(true); });
    const u2 = onSnapshot(collection(db, RUNS), (snap) => setRuns(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setRuns([]));
    return () => { u1(); u2(); };
  }, []);

  // "After another routine": finishing one makes the next one's run (quiet — the finish screen offers it).
  useEffect(() => onRoutineHook((h) => { if (h.when === 'finish') fireRoutineEvent({ type: 'after', routineId: h.routineId, at: Date.now() }); }), []);

  const routines = useMemo(() => raw.map(normalizeRoutine).filter((r) => r && routineVisible(r, uid)).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)), [raw, uid]);
  const myRuns = useMemo(() => runs.filter((r) => !uid || !r.uid || r.uid === uid), [runs, uid]);
  const current = useMemo(() => activeRun(myRuns, uid), [myRuns, uid]);

  // ---- editing ----
  const saveRoutine = useCallback(async (input) => {
    const { doc: d, project } = prepareRoutine(input, { uid, projects, departments: rawDepartments || departments || [], now: Date.now(), newId });
    const batch = writeBatch(db);
    if (project) batch.set(doc(db, 'projects', project.id), project);
    batch.set(doc(db, PROCESSES, d.id), d);
    await batch.commit();
    return d;
  }, [uid, projects, rawDepartments, departments]);
  const deleteRoutine = useCallback((id) => deleteDoc(doc(db, PROCESSES, id)), []);
  const setRoutineEnabled = useCallback((id, enabled) => updateDoc(doc(db, PROCESSES, id), { enabled: !!enabled, updatedAt: Date.now() }), []);

  // ---- running ----
  const over = () => ({ routines: raw, runs, uid, departments: rawDepartments || departments || [] });
  const act = (fn, input) => {
    const r = runClockCommand(fn, input, over());
    if (r?.hooks?.length) runRoutineHooks(r.hooks);
    return r;
  };
  const startRoutine = (routineId, opts = {}) => act(commands.startRoutine, { routineId, ...opts });
  const stepDone = (runId, opts = {}) => act(commands.routineStepDone, { runId, ...opts });
  const skipStep = (runId, opts = {}) => act(commands.routineSkipStep, { runId, ...opts });
  // Back a step (the last done/skipped one goes back on; a finished run opens again), and throw a run away.
  const backStep = (runId) => act(commands.routineBackStep, { runId });
  const resetRun = (runId) => act(commands.resetRun, { runId });
  const pauseRoutine = (runId) => act(commands.pauseRoutine, { runId });
  const resumeRoutine = (runId) => act(commands.resumeRoutine, { runId });
  const finishRoutine = (runId, extra = {}) => act(commands.finishRoutine, { runId, extra });
  const skipRoutine = (routineId, opts = {}) => act(commands.skipRoutine, { routineId, ...opts });
  // Something got missed mid-run: a step right after the current one (keep = the routine gets it for next time).
  const insertStep = (runId, opts = {}) => act(commands.routineInsertStep, { runId, ...opts });
  // A routine that asks: which project this run is for (its steps, time and the clock move there).
  const setRunProject = (runId, projectId) => act(commands.routineSetProject, { runId, projectId: projectId || null });

  // One improvements note per routine (made on first use): each line is stamped with the time and the step you're on.
  const addImprovement = async (routineId, text, { stepTitle = null } = {}) => {
    const r = raw.find((x) => x.id === routineId);
    const body = String(text || '').trim();
    if (!r || !body) return null;
    const at = new Date();
    const stamp = `${localDateStr(at)} ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}${stepTitle ? ` · ${stepTitle}` : ''}`;
    const line = textToHtml(`${stamp} — ${body}`);
    const snap = r.noteId ? await getDoc(doc(db, 'notes', r.noteId)).catch(() => null) : null;   // not the list: it may not be loaded yet
    const existing = snap?.exists() && !snap.data().deletedAt ? r.noteId : null;
    if (!existing) {
      const id = addNote({ title: `${r.name} — improvements`, html: line, tags: ['routine', 'improvements'] });
      await updateDoc(doc(db, PROCESSES, routineId), { noteId: id, updatedAt: Date.now() });
      return id;
    }
    const cur = await loadBody(existing);
    await saveBody(existing, `${cur.html || ''}${line}`);
    return existing;
  };

  // A step ticked off anywhere else (its card in the job, the plan, another device) catches the run up.
  useEffect(() => {
    if (!current) return;
    let r;
    try { r = commands.routineReconcile({ routines: raw, runs, tasks, now: Date.now }, { runId: current.id }); } catch { return; }
    if (r.changed) { try { act(commands.routineReconcile, { runId: current.id }); } catch (e) { console.warn('routine reconcile', e); } }
  }, [current?.id, current?.updatedAt, tasks]);

  const captureValue = async (runId, stepId, value) => {
    const r = commands.routineCapture({ runs }, { runId, stepId, value });
    await updateDoc(doc(db, RUNS, runId), r.writes[0].data);
  };
  const today = useCallback((now = Date.now()) => todayRoutines(raw, myRuns, { now, uid }), [raw, myRuns, uid]);

  // ---- the old Processes API (kept while screens move over) ----
  const addProcess = useCallback(async (data) => saveRoutine(data), [saveRoutine]);
  const updateProcess = useCallback((id, updates) => updateDoc(doc(db, PROCESSES, id), { ...updates, updatedAt: Date.now() }), []);
  const executeProcess = useCallback((process, scheduledDate, triggerType, triggerDetail, triggerContext) =>
    runProcess(typeof process === 'string' ? raw.find((p) => p.id === process) : process, scheduledDate, triggerType, triggerDetail || null, triggerContext || null), [raw]);

  return (
    <ProcessContext.Provider value={{
      routines, rawRoutines: raw, runs: myRuns, allRuns: runs, activeRun: current, loaded, uid, tasks,
      saveRoutine, deleteRoutine, setRoutineEnabled, today,
      startRoutine, stepDone, skipStep, backStep, resetRun, pauseRoutine, resumeRoutine, finishRoutine, skipRoutine, captureValue, insertStep, addImprovement, setRunProject,
      processes: raw, addProcess, updateProcess, deleteProcess: deleteRoutine, executeProcess,
    }}>
      {children}
    </ProcessContext.Provider>
  );
}

export function useProcesses() {
  const context = useContext(ProcessContext);
  if (!context) throw new Error('useProcesses must be used within ProcessProvider');
  return context;
}
export const useRoutines = useProcesses;
